import { KebabMenu } from '@modules/common/components/KebabMenu';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { ConfirmUserSuspensionDialog } from '@modules/users/components/UsersTable/ConfirmUserSuspensionDialog';
import { useUpdateUserState, useUpdateUserType } from '@modules/users/hooks';
import { UserRow, UserRowRoleType } from '@modules/users/store/types';
import { trackUserManagementEvents } from '@modules/workspaceSettings/utils';
import { Box, CircularProgress, SxProps } from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import { UserState } from 'generated/graphql';
import { useGetUser } from 'v2/core/services/user';

const SX_PROPS: SxProps = {
  position: 'absolute',
  right: 20,
  top: 0,
  height: '98%',
};

interface UserRoleActionsMenuProps {
  params: GridRenderCellParams<UserRow, UserRowRoleType, unknown>;
}

export const UserRoleActionsMenu = ({ params }: UserRoleActionsMenuProps) => {
  const { id: userID } = useGetUser();

  const { openModal, closeModal } = useMuiModalContext();

  const { updateUserType, isLoading: isUserTypeUpdateInProgress } =
    useUpdateUserType();
  const { updateUserState, isLoading: isUserStateUpdateInProgress } =
    useUpdateUserState();

  const { id, role, email } = params.row;

  if (id === userID) {
    return null;
  }

  const convertUserRole = () => {
    updateUserType(
      id,
      role === UserRowRoleType.Member
        ? UserRowRoleType.Admin
        : UserRowRoleType.Member,
      email,
    );
    trackUserManagementEvents({
      eventName:
        role === UserRowRoleType.Member
          ? 'User Management - Converted User to Admin Role'
          : 'User Management - Converted User to Member Role',
      userEmail: email,
    });
  };

  const suspendOrReactivateUser = (state: UserState) => {
    updateUserState(id, state, email);
  };

  const getMenuItems = () => {
    if (role === UserRowRoleType.Suspended) {
      return [
        {
          label: 'Reactivate User',
          onClick: () => {
            suspendOrReactivateUser(UserState.Active);
            trackUserManagementEvents({
              eventName: 'User Management - Reactivated User From Workspace',
              userEmail: email,
            });
          },
        },
      ];
    }

    return [
      {
        label:
          role === UserRowRoleType.Member
            ? 'Convert To Admin'
            : 'Convert To Member',
        onClick: convertUserRole,
      },
      {
        label: <Box color="error.light">Suspend User</Box>,
        onClick: () => {
          openModal({
            contentComponent: () => (
              <ConfirmUserSuspensionDialog
                email={email}
                onCancel={() => closeModal()}
                onConfirm={() => {
                  suspendOrReactivateUser(UserState.Suspended);
                  closeModal();
                  trackUserManagementEvents({
                    eventName:
                      'User Management - Suspended User From Workspace',
                    userEmail: email,
                  });
                }}
              />
            ),
            modalProps: {
              fullWidth: true,
              maxWidth: 'sm',
              PaperProps: {
                elevation: 3,
              },
            },
          });
        },
      },
    ];
  };

  const isLoading = isUserTypeUpdateInProgress || isUserStateUpdateInProgress;

  return (
    <Box>
      {isLoading && (
        <Box sx={SX_PROPS}>
          <CircularProgress size={35} />
        </Box>
      )}
      <KebabMenu
        menuItems={getMenuItems()}
        buttonProps={{
          color: 'primary',
          className: 'js-row-actions-menu',
          sx: {
            ...SX_PROPS,
            opacity: 0,
            display: isLoading ? 'none' : undefined,
          },
        }}
        menuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'right',
          },
        }}
      />
    </Box>
  );
};
